/**
 * État global de l'application : session, réseau, file d'attente, routage.
 *
 * Pas de bibliothèque de routage ni de gestion d'état : un routeur à fragment
 * (`#/maison/e1`) et un contexte React suffisent pour une application de cette
 * taille, et ça évite deux dépendances à suivre pendant des années.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { Famille, PeriodeVacances, Tache, Utilisateur } from '@foyer/noyau';

import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

import {
  type Operation,
  ErreurApi,
  file,
  lit,
  memoriseJeton,
  memoriseSession,
  oublieTout,
  sessionMemorisee,
  synchronise,
} from './api.js';
import { donnees } from './donnees.js';
import { LIEN_APPLI } from './google.js';
import { estNatif, reprogrammeRappels } from './rappels-locaux.js';

// ── Routage ─────────────────────────────────────────────────────────────────

export function cheminCourant(): string {
  const brut = window.location.hash.replace(/^#/, '');
  return brut || '/aujourdhui';
}

export function navigue(chemin: string): void {
  window.location.hash = chemin;
}

export function useRoute(): string[] {
  const [chemin, setChemin] = useState(cheminCourant);
  useEffect(() => {
    const surChangement = () => {
      setChemin(cheminCourant());
      // Changer d'écran remonte en haut : sur mobile, rester au milieu d'une
      // liste après une navigation est déroutant.
      window.scrollTo({ top: 0 });
    };
    window.addEventListener('hashchange', surChangement);
    return () => window.removeEventListener('hashchange', surChangement);
  }, []);
  return chemin.split('/').filter(Boolean);
}

// ── Contexte ────────────────────────────────────────────────────────────────

export interface EtatAppli {
  chargement: boolean;
  utilisateur: Utilisateur | null;
  famille: Famille | null;
  enLigne: boolean;
  /** Opérations en attente de synchronisation. */
  enAttente: Operation[];
  /** Incrémenté à chaque écriture réussie : les vues s'y abonnent pour se recharger. */
  revision: number;
  /** À appeler après une écriture, pour rafraîchir les vues. */
  rafraichis(): void;
  connecte(utilisateur: Utilisateur, famille: Famille): void;
  /**
   * Remplace l'utilisateur courant après un changement de réglage. Sans ça, un
   * réglage qui gouverne l'affichage — le filtre de l'accueil — ne prendrait
   * effet qu'au rechargement complet de l'application.
   */
  majUtilisateur(utilisateur: Utilisateur): void;
  deconnecte(): Promise<void>;
  synchroniseMaintenant(): Promise<void>;
}

const Contexte = createContext<EtatAppli | null>(null);

export function useAppli(): EtatAppli {
  const etat = useContext(Contexte);
  if (!etat) throw new Error('useAppli hors de FournisseurAppli');
  return etat;
}

export function FournisseurAppli({ children }: { children: ReactNode }) {
  const [chargement, setChargement] = useState(true);
  const [utilisateur, setUtilisateur] = useState<Utilisateur | null>(null);
  const [famille, setFamille] = useState<Famille | null>(null);
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [enAttente, setEnAttente] = useState<Operation[]>(file);
  const [revision, setRevision] = useState(0);

  const rafraichis = useCallback(() => {
    setEnAttente(file());
    setRevision((r) => r + 1);
  }, []);

  const synchroniseMaintenant = useCallback(async () => {
    if (file().length === 0) return;
    await synchronise();
    rafraichis();
  }, [rafraichis]);

  // Au démarrage : on ne demande la session que si on en a mémorisé une, pour
  // éviter un 401 inutile au premier lancement.
  useEffect(() => {
    let vivant = true;
    (async () => {
      if (!sessionMemorisee()) {
        setChargement(false);
        return;
      }
      try {
        const { utilisateur: u, famille: f } = await donnees.moi();
        if (!vivant) return;
        setUtilisateur(u);
        setFamille(f);
      } catch (err) {
        if (err instanceof ErreurApi && err.statut === 401) memoriseSession(false);
      } finally {
        if (vivant) setChargement(false);
      }
    })();
    return () => {
      vivant = false;
    };
  }, []);

  /**
   * Retour de la connexion Google faite dans le navigateur.
   *
   * Google refuse ses pages de connexion dans une WebView embarquée, donc
   * l'application ouvre le site dans un onglet système ; celui-ci lui rend la
   * main par `ovh.mainedufour.foyer://auth?jeton=…`. On mémorise le jeton, on
   * ferme l'onglet, et on demande qui on est.
   *
   * Le listener est posé même sans session : c'est précisément quand il n'y en a
   * pas qu'il sert.
   */
  useEffect(() => {
    if (!estNatif()) return;
    let poignee: { remove: () => void } | undefined;
    let vivant = true;
    void CapApp.addListener('appUrlOpen', async ({ url }: { url: string }) => {
      if (!url?.startsWith(LIEN_APPLI)) return;
      const recu = new URLSearchParams(url.split('?')[1] ?? '').get('jeton');
      if (!recu) return;
      memoriseJeton(recu);
      memoriseSession(true);
      try {
        await Browser.close();
      } catch {
        // L'onglet peut déjà être fermé : ce n'est pas une erreur.
      }
      try {
        const { utilisateur: u, famille: f } = await donnees.moi();
        if (!vivant) return;
        setUtilisateur(u);
        setFamille(f);
        setRevision((r) => r + 1);
      } catch {
        // Jeton refusé : on retombe sur l'écran de connexion, sans message
        // cryptique — la seule action utile est de recommencer.
        memoriseJeton(null);
        memoriseSession(false);
      }
    }).then((h) => {
      poignee = h;
    });
    return () => {
      vivant = false;
      poignee?.remove();
    };
  }, []);

  // Retour du réseau : on rejoue la file sans rien demander à l'utilisateur.
  useEffect(() => {
    const enHaut = () => {
      setEnLigne(true);
      void synchroniseMaintenant();
    };
    const enBas = () => setEnLigne(false);
    window.addEventListener('online', enHaut);
    window.addEventListener('offline', enBas);
    return () => {
      window.removeEventListener('online', enHaut);
      window.removeEventListener('offline', enBas);
    };
  }, [synchroniseMaintenant]);

  // Et au premier chargement, si des opérations attendaient depuis la veille.
  useEffect(() => {
    if (enLigne && utilisateur) void synchroniseMaintenant();
  }, [enLigne, utilisateur, synchroniseMaintenant]);

  /**
   * Reprogrammation des rappels de l'application installée.
   *
   * Ici plutôt que dans un écran : les rappels doivent suivre les données même
   * si l'utilisateur ne visite jamais la page Notifications. `revision` change à
   * chaque écriture réussie, donc cocher une tâche décale bien son prochain
   * rappel.
   *
   * Sans effet sur le web — `reprogrammeRappels` rend la main immédiatement
   * quand la plateforme n'est pas native. Et en meilleur effort : un échec de
   * programmation ne doit jamais empêcher l'application de fonctionner, il est
   * retenu et montré dans l'écran Notifications.
   */
  useEffect(() => {
    if (!estNatif() || !utilisateur) return;
    let vivant = true;
    (async () => {
      try {
        const [t, v] = await Promise.all([
          lit<{ taches: Tache[] }>('/api/taches?statut=active'),
          lit<{ periodes: PeriodeVacances[] }>('/api/vacances'),
        ]);
        if (!vivant) return;
        // Le rythme de relance vient de l'utilisateur connecté : sans lui, le
        // planificateur embarqué retomberait sur « hebdomadaire » et le réglage
        // resterait sans effet sur le seul canal que le téléphone reçoive.
        await reprogrammeRappels(t.taches, v.periodes, utilisateur.relanceRetard);
      } catch {
        // Hors ligne au démarrage : les rappels déjà programmés tiennent, et la
        // prochaine synchronisation reprendra la main.
      }
    })();
    return () => {
      vivant = false;
    };
  }, [utilisateur, revision]);

  const valeur = useMemo<EtatAppli>(
    () => ({
      chargement,
      utilisateur,
      famille,
      enLigne,
      enAttente,
      revision,
      rafraichis,
      connecte(u, f) {
        memoriseSession(true);
        setUtilisateur(u);
        setFamille(f);
        setRevision((r) => r + 1);
      },
      majUtilisateur(u) {
        // Pas de `revision` ici : le filtre se calcule au rendu, rien n'est à
        // redemander au serveur.
        setUtilisateur(u);
      },
      async deconnecte() {
        try {
          await donnees.deconnexion();
        } catch {
          // Déconnexion locale de toute façon : le cookie sera périmé.
        }
        oublieTout();
        setUtilisateur(null);
        setFamille(null);
        navigue('/aujourdhui');
      },
      synchroniseMaintenant,
    }),
    [chargement, utilisateur, famille, enLigne, enAttente, revision, rafraichis, synchroniseMaintenant],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

// ── Chargement de données ───────────────────────────────────────────────────

export interface Ressource<T> {
  donnee: T | null;
  chargement: boolean;
  erreur: string | null;
  recharge(): void;
}

/**
 * Charge une ressource et la recharge quand la révision globale change. Le cache
 * du client fait que, hors ligne, `donnee` arrive quand même — avec les valeurs
 * de la dernière visite.
 */
export function useRessource<T>(charge: () => Promise<T>, dependances: unknown[] = []): Ressource<T> {
  const { revision } = useAppli();
  const [donnee, setDonnee] = useState<T | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [local, setLocal] = useState(0);

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    charge()
      .then((d) => {
        if (!vivant) return;
        setDonnee(d);
        setErreur(null);
      })
      .catch((err: Error) => {
        if (vivant) setErreur(err.message);
      })
      .finally(() => {
        if (vivant) setChargement(false);
      });
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, local, ...dependances]);

  return { donnee, chargement, erreur, recharge: () => setLocal((n) => n + 1) };
}
